import { staysOf } from "../../rail/StayAt/stay";
import { flashbackSelfHost } from "@/data/flashback-selves";
import type { CharacterStay } from "@/data/rail-branches";
import { MARKER_JOIN_MS } from "../MarkerPresence/presence";
import type { MarkerCircleOffset } from "@/lib/timeline/scene/SceneMotion/motion";

/** Ease a newcomer out from someone already seated, instead of the group center. */
export function markerAppearanceT(
  stays: readonly CharacterStay[],
  characterId: string,
  stay: CharacterStay,
  playheadMs: number,
  minorCharacterIds?: readonly string[],
): number {
  if (
    stay.startMs <= 0
    || continuesStay(stays, characterId, stay)
    || minorDebutSkipsHostEmergence(
      stays,
      characterId,
      stay,
      minorCharacterIds,
    )
  ) {
    return 1;
  }
  const raw = clamp01((playheadMs - stay.startMs) / MARKER_JOIN_MS);
  return easeOutCubic(raw);
}

export function emergeNameOpacity(t: number): number {
  if (t >= 1) {
    return 1;
  }
  return Math.max(0, (t - 0.45) / 0.55);
}

export function emergeOffset(
  from: MarkerCircleOffset,
  to: MarkerCircleOffset,
  t: number,
): MarkerCircleOffset {
  return {
    dx: from.dx + (to.dx - from.dx) * t,
    dy: from.dy + (to.dy - from.dy) * t,
  };
}

/** Someone already present, preferring the same place and the nearest seat. */
export function pickHostId(
  stays: readonly CharacterStay[],
  characterId: string,
  stay: CharacterStay,
  seatAt: (id: string) => MarkerCircleOffset | null,
): string | null {
  return nearestHostId(hostPool(stays, characterId, stay), seatAt(characterId), seatAt);
}

const poolByStay = new WeakMap<CharacterStay, string[]>();

/** A stay's pool never changes, and a long book asks for it constantly. */
function hostPool(
  stays: readonly CharacterStay[],
  characterId: string,
  stay: CharacterStay,
): string[] {
  const known = poolByStay.get(stay);
  if (known !== undefined) {
    return known;
  }
  const pool = findHostPool(stays, characterId, stay);
  poolByStay.set(stay, pool);
  return pool;
}

function findHostPool(
  stays: readonly CharacterStay[],
  characterId: string,
  stay: CharacterStay,
): string[] {
  if (stay.emergesFrom !== undefined) {
    return [stay.emergesFrom];
  }
  const at = stay.startMs;
  const original = flashbackSelfHost(stays, characterId, at);
  if (original !== null) {
    return [original];
  }
  const samePlace: string[] = [];
  const elsewhere: string[] = [];
  const seen = new Set<string>();
  for (const item of stays) {
    if (item.characterId === characterId || seen.has(item.characterId)) {
      continue;
    }
    if (item.startMs >= at || item.endMs < at) {
      continue;
    }
    seen.add(item.characterId);
    const bucket = item.rootPlaceId === stay.rootPlaceId ? samePlace : elsewhere;
    bucket.push(item.characterId);
  }
  return samePlace.length > 0 ? samePlace : elsewhere;
}

export function nearestHostId(
  pool: readonly string[],
  mine: MarkerCircleOffset | null,
  seatAt: (id: string) => MarkerCircleOffset | null,
): string | null {
  let best: string | null = null;
  let bestDist = Number.POSITIVE_INFINITY;
  for (const id of pool) {
    const seat = seatAt(id);
    const dist = mine === null || seat === null
      ? Number.POSITIVE_INFINITY
      : Math.hypot(seat.dx - mine.dx, seat.dy - mine.dy);
    if (best === null || dist < bestDist || (dist === bestDist && id < best)) {
      best = id;
      bestDist = dist;
    }
  }
  return best;
}

function continuesStay(
  stays: readonly CharacterStay[],
  characterId: string,
  stay: CharacterStay,
): boolean {
  return staysOf(stays, characterId).some((item) =>
    item.startMs < stay.startMs &&
    item.endMs >= stay.startMs,
  );
}

function minorDebutSkipsHostEmergence(
  stays: readonly CharacterStay[],
  characterId: string,
  stay: CharacterStay,
  minorCharacterIds?: readonly string[],
): boolean {
  if (minorCharacterIds === undefined || !minorCharacterIds.includes(characterId)) {
    return false;
  }
  return firstStayInChapter(stays, characterId, stay);
}

/** First rail stay for this character in the chapter. */
function firstStayInChapter(
  stays: readonly CharacterStay[],
  characterId: string,
  stay: CharacterStay,
): boolean {
  return !staysOf(stays, characterId).some((item) => item.startMs < stay.startMs);
}

function easeOutCubic(t: number): number {
  return 1 - (1 - t) ** 3;
}

function clamp01(value: number): number {
  return Math.min(1, Math.max(0, value));
}
